import './CameraRecorder.css';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export type RecordQualityKey = 'low' | 'medium' | 'medium_high' | 'high' | 'max';

const RECORD_QUALITY_BPS: Record<RecordQualityKey, number> = {
  low: 1_000_000,
  medium: 2_500_000,
  medium_high: 3_750_000,
  high: 5_000_000,
  max: 8_000_000,
};

const MAX_RECORD_SECONDS = 40;
// This caps the UI, api limits to 45 seconds in getStreamUploadUrl function

export interface CameraRecorderProps {
  quality?: RecordQualityKey;
  onCapture: (blob: Blob, mimeType: string) => void;
  onClose: () => void;
}

function getBestMimeType(): string {
  const candidates = [
    'video/mp4;codecs=h264,aac',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return candidates.find((c) => MediaRecorder.isTypeSupported(c)) ?? '';
}

export default function CameraRecorder({
  quality = 'medium',
  onCapture,
  onClose,
}: CameraRecorderProps): React.ReactElement {
  const [camStatus, setCamStatus] = useState<'requesting' | 'preview' | 'recording' | 'error'>('requesting');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [videoReady, setVideoReady] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const recordStartMsRef = useRef<number>(0);

  const stopStream = (): void => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const clearTimer = (): void => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startCamera = async (facing: 'environment' | 'user'): Promise<void> => {
    setCamStatus('requesting');
    setVideoReady(false);
    stopStream();
    try {
      // Always request ideal 1080p; browser falls back gracefully.
      // Input resolution is the primary quality driver on iOS Safari.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: true,
      });
      streamRef.current = stream;
      if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;
      setCamStatus('preview');
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Camera access denied');
      setCamStatus('error');
    }
  };

  // Syncing with external system (MediaDevices API) — useEffect is appropriate.
  useEffect(() => {
    void startCamera('environment');
    return () => { clearTimer(); stopStream(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // PORT NOTE — useCallback with [] is intentional: keeps the ref callback stable
  // across re-renders (e.g. timer ticks) so srcObject is not reassigned every second,
  // which would cause the live preview to flash black. streamRef/liveVideoRef are refs
  // so the stale closure is safe — .current always reflects current values.
  const handleLiveVideoRef = useCallback((el: HTMLVideoElement | null): void => {
    liveVideoRef.current = el;
    if (el && streamRef.current) el.srcObject = streamRef.current;
  }, []);

  const handleFlip = (): void => {
    const current = streamRef.current?.getVideoTracks()[0]?.getSettings().facingMode;
    void startCamera(current === 'environment' ? 'user' : 'environment');
  };

  const startRecording = (): void => {
    if (!streamRef.current) return;
    const mime = getBestMimeType();
    chunksRef.current = [];
    const recorder = new MediaRecorder(streamRef.current, {
      mimeType: mime || undefined,
      videoBitsPerSecond: RECORD_QUALITY_BPS[quality],
    });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime || 'video/webm' });
      stopStream();
      onCapture(blob, mime);
    };
    recorderRef.current = recorder;
    recordStartMsRef.current = Date.now();
    setElapsedMs(0);
    setCamStatus('recording');
    // 100ms tick keeps the progress bar smooth without burning frames; the
    // exact cap is enforced by comparing elapsed against MAX_RECORD_SECONDS.
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - recordStartMsRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= MAX_RECORD_SECONDS * 1000) {
        stopRecording();
      }
    }, 100);
    // PORT NOTE — defer via rAF to prevent 2–4 black frames at recording start
    // (iOS Safari + Chrome hardware encoder reconfigures mid-paint). Deferring
    // lets the current paint cycle finish before the encoder kicks in.
    // Also no timeslice arg: recorder.start(100) causes repeated pipeline stalls.
    requestAnimationFrame(() => recorder.start());
  };

  const stopRecording = (): void => {
    clearTimer();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const handleClose = (): void => {
    clearTimer();
    recorderRef.current?.stop();
    stopStream();
    onClose();
  };

  const isRecording = camStatus === 'recording';
  const canRecord = camStatus === 'preview' || camStatus === 'recording';
  const elapsedSec = Math.min(MAX_RECORD_SECONDS, Math.floor(elapsedMs / 1000));
  const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
  const ss = String(elapsedSec % 60).padStart(2, '0');
  const progressPct = Math.min(100, (elapsedMs / (MAX_RECORD_SECONDS * 1000)) * 100);

  return createPortal(
    <div className="cam-recorder">
      {/*
       * iOS Safari briefly lays the <video> out at its 300×150 intrinsic
       * size before the stream's metadata arrives, which renders one frame
       * of a tiny centered preview. We hide the element until `loadedmetadata`
       * (or a fallback `playing`) so the user only sees the black shell
       * during that gap instead of the small preview.
       */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={handleLiveVideoRef}
        className={`cam-recorder-video${videoReady ? ' cam-recorder-video--ready' : ''}`}
        autoPlay
        muted
        playsInline
        onLoadedMetadata={() => setVideoReady(true)}
        onPlaying={() => setVideoReady(true)}
      />

      <div className="cam-recorder-top">
        {isRecording ? (
          <div
            className="cam-recorder-progress-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={MAX_RECORD_SECONDS}
            aria-valuenow={elapsedSec}
            aria-label="Recording progress"
          >
            <div
              className="cam-recorder-progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        ) : null}
        <div className="cam-recorder-top-content">
          {isRecording ? (
            <span className="cam-recorder-timer">
              <span className="cam-recorder-rec-dot" aria-hidden="true" />
              {mm}:{ss}
            </span>
          ) : (
            <span className="cam-recorder-hint">{MAX_RECORD_SECONDS} second video maximum</span>
          )}
        </div>
      </div>

      {camStatus === 'error' && (
        <div className="cam-recorder-error-overlay">
          <p className="cam-recorder-error-msg">{errorMsg}</p>
          <button
            type="button"
            className="cam-recorder-icon-btn"
            onClick={() => void startCamera('environment')}
          >
            Retry
          </button>
        </div>
      )}

      <div className="cam-recorder-bottom">
        <button
          type="button"
          className="cam-recorder-icon-btn"
          onClick={handleClose}
          aria-label="Close camera"
        >
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </button>
        <button
          type="button"
          className="cam-recorder-shutter"
          onClick={isRecording ? stopRecording : startRecording}
          disabled={!canRecord}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          <span className={`cam-recorder-shutter-inner${isRecording ? ' cam-recorder-shutter-inner--stop' : ''}`} />
        </button>
        <button
          type="button"
          className="cam-recorder-icon-btn"
          onClick={handleFlip}
          disabled={isRecording}
          aria-label="Flip camera"
        >
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20" aria-hidden="true">
            <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>,
    document.body,
  );
}
